import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Send } from "@/lib/icons";

export default function Collaborate() {
  const { user } = useAuth();
  const canExport = user?.role === "owner";
  const [members, setMembers] = useState([]);
  useEffect(() => {
    api.get("/settings").then((r) => setMembers(r.data.members || [])).catch(() => {});
  }, []);

  const exportData = async () => {
    try {
      const { data } = await api.get("/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `docklinc-export-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Workspace export downloaded");
    } catch {
      toast.error("Could not export workspace data. Check your access and try again.");
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader eyebrow="Communicate" color="#14B8A6" title="Communicate"
        subtitle="Move selected knowledge beyond the private workspace, deliberately." />

      <div className="dl-card p-6 mb-5">
        <div className="flex items-start gap-4">
          <span className="h-12 w-12 rounded-xl grid place-items-center shrink-0" style={{ background: "#14B8A622", color: "#14B8A6" }}><Download size={22} /></span>
          <div className="flex-1">
            <h2 className="font-display text-lg text-white">Export workspace data</h2>
            <p className="text-sm text-slate-400 mt-1">Download readable records, relationships, and a media manifest. The endpoint checks owner access.</p>
            {canExport ? (
              <Button onClick={exportData} className="gap-2 mt-4" style={{ background: "#0F766E" }} data-testid="communicate-export-btn">
                <Download size={15} /> Download export
              </Button>
            ) : (
              <p className="text-xs text-slate-500 mt-4">Workspace export is available to the workspace owner.</p>
            )}
          </div>
        </div>
      </div>

      <div className="dl-card p-6">
        <div className="flex items-start gap-4">
          <span className="h-12 w-12 rounded-xl grid place-items-center shrink-0" style={{ background: "#14B8A622", color: "#14B8A6" }}><Send size={22} /></span>
          <div>
            <h2 className="font-display text-lg text-white">Sharing and publishing</h2>
            <p className="text-sm text-slate-400 mt-1">
              Invitations, collection sharing, comments, and approved publishing connectors are planned. DockLinc keeps the workspace private by default; each future send or publish action will be explicit.
            </p>
            <span className="inline-flex mt-4 text-[10px] px-2 py-1 rounded-full" style={{ background: "#64748B22", color: "#CBD5E1" }}>Planned for later stages</span>
          </div>
        </div>
      </div>

      {members.length > 0 && (
        <div className="dl-card p-5 mt-5">
          <div className="font-mono-label text-[11px] text-slate-500 mb-3">Current workspace members</div>
          {members.map((member) => (
            <div key={member.id} className="flex items-center gap-3 py-2">
              <span className="h-8 w-8 rounded-full grid place-items-center text-white text-xs font-semibold" style={{ background: "#0F766E" }}>
                {(member.name || member.email || "U").slice(0, 1).toUpperCase()}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{member.name || member.email}</div>
                {member.email && <div className="text-[11px] text-slate-500 truncate">{member.email}</div>}
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full capitalize" style={{ background: "#14B8A622", color: "#5EEAD4" }}>{member.role}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
